import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Loader } from 'semantic-ui-react';
import moment from 'moment-timezone';
import { listFeed } from '../../services/Feed';
import { fetchCommunityNews } from '../../services/CommunityNews';
import { fetchAllCommunityAnnouncements } from '../../services/Announcements';
import { fetchAllResidentGroups } from '../../services/Groups';
import { getStandaloneSignageImage } from '../../services/ImageUploader';
import { FeedItem } from '../../types/FeedItem';
import { CommunityNewsType } from '../../types/CommunityNews';
import { Announcement } from '../../types/Announcement';
import { Group } from '../../types/Group';
import FeedItemCard from './FeedItemCard';
import sendToast from '../../../utils/Toast';
import { normalizeId } from '../../../utils/normalizeId';
import useResidentIdentities from '../../hooks/useResidentIdentities';
import { useFacility } from '../../contexts/facility/FacilityContext';

interface Props {
    facilityId: string;
    currentResidentId: string;
    groupResidentIds?: string[];
    groupStaffIds?: string[];
    selectedGroupIds?: string[];
    onProfileClick?: (registrantId: string) => void;
    // Both tabs stay mounted (see resident-social/index.tsx), so this tells the
    // fetch effect whether it's the one actually visible right now.
    isActive: boolean;
}

const PAGE_SIZE = 20;
// Most pages requested automatically when a group filter empties a full server page.
const MAX_EMPTY_PAGE_RETRIES = 5;
const NEWS_ROW_PREFIX = 'communityNews:';
const ANNOUNCEMENT_LIST_PREFIX = 'listAnnouncements:';
const isMongoId = (value?: string) => !!value && /^[a-f0-9]{24}$/i.test(value);
const usableImage = (value?: string | null) => (value && !isMongoId(value) ? value : undefined);
const usableName = (name?: string | null) => {
    const value = (name || '').trim();
    return value && !isMongoId(value) ? value : '';
};
const staffFullName = (staff?: { FirstName?: string; LastName?: string }) =>
    usableName(`${staff?.FirstName || ''} ${staff?.LastName || ''}`);
const creatorIds = (item: Announcement) => {
    const creator = item.creatorInfo as { id?: string; Id?: string; firstName?: string; FirstName?: string } | undefined;
    const firstName = creator?.firstName || creator?.FirstName;
    return [creator?.id, creator?.Id, isMongoId(firstName) ? firstName : undefined, item.AddedBy]
        .map(normalizeId)
        .filter(Boolean);
};

const toMillis = (dateAdded?: string | number | null, timezone?: string) => {
    if (dateAdded === null || dateAdded === undefined || dateAdded === '') return 0;
    if (typeof dateAdded === 'number' && Number.isFinite(dateAdded)) {
        return dateAdded < 1_000_000_000_000 ? dateAdded * 1000 : dateAdded;
    }
    const raw = String(dateAdded).trim();
    if (!raw) return 0;
    if (/^\d+(\.\d+)?$/.test(raw)) {
        const numericDate = Number(raw);
        return numericDate < 1_000_000_000_000 ? numericDate * 1000 : numericDate;
    }
    const parsed = timezone ? moment.tz(raw, timezone) : moment(raw);
    return parsed.isValid() ? parsed.valueOf() : 0;
};

const normalizeAnnouncementText = (text?: string) =>
    (text || '').replace(/\s+/g, ' ').replace(/[.\s]+$/g, '').trim().toLowerCase();

const announcementIdentity = (item: FeedItem) => {
    if (item._id.startsWith(ANNOUNCEMENT_LIST_PREFIX)) {
        return normalizeId(item._id.slice(ANNOUNCEMENT_LIST_PREFIX.length));
    }
    const raw = item as FeedItem & { AnnouncementId?: string };
    return [item.announcementId, raw.AnnouncementId]
        .map(normalizeId)
        .find((id) => isMongoId(id)) || '';
};

const facilityDayKey = (dateAdded?: string | number | null, timezone?: string) => {
    const ms = toMillis(dateAdded, timezone);
    if (!ms) return '';
    const parsed = timezone ? moment.tz(ms, timezone) : moment(ms);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const isSameAnnouncement = (existing: FeedItem, extra: FeedItem, timezone?: string) => {
    if (existing.Category !== 'Announcement' || extra.Category !== 'Announcement') return false;
    const existingId = announcementIdentity(existing);
    const extraId = announcementIdentity(extra);
    if (existingId && extraId) return existingId === extraId;
    const existingMsg = normalizeAnnouncementText(existing.AnnouncementMessage);
    const extraMsg = normalizeAnnouncementText(extra.AnnouncementMessage);
    if (!existingMsg || !extraMsg || existingMsg !== extraMsg) return false;
    const existingDay = facilityDayKey(existing.DateAdded, timezone);
    const extraDay = facilityDayKey(extra.DateAdded, timezone);
    return !!existingDay && existingDay === extraDay;
};

const FeedView = ({ facilityId, currentResidentId, groupResidentIds, groupStaffIds, selectedGroupIds, onProfileClick, isActive }: Props) => {
    const [feedItems, setFeedItems] = useState<FeedItem[]>([]);
    const [feedPageNo, setFeedPageNo] = useState(1);
    const [newsPageNo, setNewsPageNo] = useState(1);
    // True until the first fetch settles. The effect that starts it runs after
    // paint, so beginning false flashes the empty state for a frame.
    const [isLoading, setIsLoading] = useState(true);
    const [hasMoreByPage, setHasMoreByPage] = useState(true);
    const [hasMoreNews, setHasMoreNews] = useState(false);
    const feedItemsRef = useRef<FeedItem[]>([]);
    const announcementExtrasRef = useRef<FeedItem[]>([]);
    feedItemsRef.current = feedItems;
    const { facilityTimezone, loading: facilityLoading } = useFacility();
    const selectedGroupIdSet = useMemo(
        () => new Set((selectedGroupIds || []).map(normalizeId).filter(Boolean)),
        [selectedGroupIds],
    );
    const groupResidentIdSet = useMemo(
        () => new Set((groupResidentIds || []).map(normalizeId).filter(Boolean)),
        [groupResidentIds],
    );
    const groupStaffIdSet = useMemo(
        () => new Set((groupStaffIds || []).map(normalizeId).filter(Boolean)),
        [groupStaffIds],
    );

    // Feed rows carry the resident name/image frozen at write time, so join the
    // registrants on screen back to the directory for their social profile. A PostComment line
    // also names the post's author, so a resident author is looked up too.
    const residentIdsInFeed = useMemo(
        () => Array.from(new Set(feedItems.flatMap(item => [
            item.RegistrantId,
            item.Category === 'PostComment' && item.PostAuthorType === 'resident' ? item.PostAuthorId : undefined,
        ]).map(normalizeId).filter(Boolean))),
        [feedItems],
    );
    const residentIdentityById = useResidentIdentities(facilityId, residentIdsInFeed);

    const applyClientSideGroupFilter = (items: FeedItem[]): FeedItem[] => {
        if (!selectedGroupIdSet.size) return items;

        return items.filter((item) => {
            // A comment line ("X commented on Y's post") follows the audience of its post, never the
            // commenter's membership. A post with groups matches on them; one without matches on who
            // wrote the post. A line with neither groups nor PostAuthorId cannot be scoped, so it is hidden.
            if (item.Category === 'PostComment') {
                const postGroupIds = (item.PostGroups || []).map(normalizeId).filter(Boolean);
                if (postGroupIds.length) return postGroupIds.some((id) => selectedGroupIdSet.has(id));
                const postAuthorId = normalizeId(item.PostAuthorId);
                if (!postAuthorId) return false;
                return (item.PostAuthorType === 'staff' ? groupStaffIdSet : groupResidentIdSet).has(postAuthorId);
            }
            const candidateResidentIds = [
                item.RegistrantId,
                item.ReceiverId,
                item.SenderId,
                item.RelativeId,
                ...(item.residentIds || []),
            ].map(normalizeId).filter(Boolean);
            const candidateStaffIds = [
                item.StaffId,
                item.ReceiverId,
                item.SenderId,
            ].map(normalizeId).filter(Boolean);

            const hasResidentMatch = candidateResidentIds.some((id) => groupResidentIdSet.has(id));
            const hasStaffMatch = candidateStaffIds.some((id) => groupStaffIdSet.has(id));
            return hasResidentMatch || hasStaffMatch;
        });
    };

    const fetchFeed = async ({
        feedPage,
        newsPage,
        reset = false,
        fetchFeedPage = true,
        fetchNewsPage = true,
        emptyPagesLeft = MAX_EMPTY_PAGE_RETRIES,
    }: {
        feedPage: number;
        newsPage: number;
        reset?: boolean;
        fetchFeedPage?: boolean;
        fetchNewsPage?: boolean;
        // How many more pages may be requested automatically when a group filter empties a full page.
        emptyPagesLeft?: number;
    }) => {
        try {
            // Set loading before clearing, so a reset never paints the empty state.
            setIsLoading(true);
            if (reset) {
                setFeedItems([]);
                setHasMoreByPage(true);
            }
            const hasSelectedGroup = selectedGroupIdSet.size > 0;
            const shouldFetchFeed = reset || fetchFeedPage;
            const shouldFetchNews = reset || fetchNewsPage;
            const nowInFacility = facilityTimezone ? moment.tz(facilityTimezone) : moment();
            const toDate = nowInFacility.valueOf();
            const fromDate = nowInFacility.clone().subtract(30, 'days').valueOf();
            const newsStartDate = nowInFacility.clone().subtract(30, 'days').format('YYYY-MM-DD');
            const newsEndDate = nowInFacility.clone().add(1, 'day').format('YYYY-MM-DD');
            const announcementsStartISO = nowInFacility.clone().subtract(30, 'days').toISOString();
            const announcementsEndISO = nowInFacility.clone().endOf('day').toISOString();
            const [response, newsResponse, announcements, groups] = await Promise.all([
                shouldFetchFeed
                    ? listFeed({
                        facilityId,
                        fromDate,
                        toDate,
                        callerResidentId: currentResidentId,
                        ResidentIds: hasSelectedGroup ? undefined : groupResidentIds,
                        StaffIds: hasSelectedGroup ? undefined : groupStaffIds,
                        GroupIds: selectedGroupIds,
                        page_no: feedPage,
                        page_size: PAGE_SIZE,
                    })
                    : Promise.resolve({ feedItems: [] as FeedItem[], count: 0 }),
                shouldFetchNews
                    ? fetchCommunityNews({
                        facilityId,
                        filter: {
                            startDate: { $lt: newsEndDate },
                            endDate: { $gte: newsStartDate },
                        },
                        pagingSorting: { page_no: newsPage, page_size: PAGE_SIZE },
                    }).catch((error) => {
                        console.error('Error fetching community news:', error);
                        sendToast('error', error instanceof Error ? error.message : 'Failed to fetch Community News');
                        return { Result: [] as CommunityNewsType[], TotRecords: 0 };
                    })
                    : Promise.resolve({ Result: [] as CommunityNewsType[], TotRecords: 0 }),
                reset && currentResidentId
                    ? fetchAllCommunityAnnouncements(facilityId, {
                        startTimestampISO: announcementsStartISO,
                        endTimestampISO: announcementsEndISO,
                        residentId: currentResidentId,
                        excludeScheduled: true,
                    }).catch((error) => {
                        console.error('Error fetching announcements:', error);
                        sendToast('error', error instanceof Error ? error.message : 'Failed to fetch Announcements');
                        return [] as Announcement[];
                    })
                    : Promise.resolve([] as Announcement[]),
                currentResidentId
                    ? fetchAllResidentGroups(facilityId, currentResidentId).catch(() => [] as Group[])
                    : Promise.resolve([] as Group[]),
            ]);
            const serverFeedItems = response.feedItems || [];
            const filteredFeedItems = applyClientSideGroupFilter(serverFeedItems);
            const news = newsResponse.Result || [];
            const sourceAnnouncements = reset ? announcements : [];
            const staffNameById = new Map<string, { name: string; image?: string }>();
            filteredFeedItems.forEach((item) => {
                const name = usableName(item.Staff_FullName);
                const id = normalizeId(item.StaffId);
                if (id && name) staffNameById.set(id, { name, image: item.StaffImage });
            });
            (groups || []).forEach((group) => {
                (group.Staff || []).forEach((staff) => {
                    const name = staffFullName(staff);
                    const id = normalizeId(staff._id);
                    if (id && name) staffNameById.set(id, { name, image: staff.Image });
                });
            });
            const resolveStaff = (ids: Array<string | undefined>) =>
                ids.map((id) => staffNameById.get(normalizeId(id))).find(Boolean);
            const extraAnnouncements: FeedItem[] = applyClientSideGroupFilter((reset ? sourceAnnouncements : []).map((item) => {
                const creator = item.creatorInfo as {
                    id?: string;
                    Id?: string;
                    firstName?: string;
                    lastName?: string;
                    FirstName?: string;
                    LastName?: string;
                    name?: string;
                    icon?: string;
                } | undefined;
                const firstName = creator?.firstName || creator?.FirstName || '';
                const lastName = creator?.lastName || creator?.LastName || '';
                const rawName = usableName(`${firstName} ${lastName}`.trim() || creator?.name);
                const staffIds = creatorIds(item);
                const staff = resolveStaff(staffIds);
                const rawAnnouncement = item as Announcement & { ResidentIds?: string[]; residentIds?: string[] };
                return {
                    _id: `${ANNOUNCEMENT_LIST_PREFIX}${item._id}`,
                    Category: 'Announcement',
                    FacilityId: item.Facility || facilityId,
                    DateAdded: (item.triggerTimestampISO || item.startDate || item.DateAdded) as string,
                    AnnouncementMessage: item.Message,
                    announcementId: item._id,
                    StaffId: staffIds[0] || creator?.id || creator?.Id,
                    SenderId: staffIds[0] || creator?.id || creator?.Id,
                    residentIds: rawAnnouncement.ResidentIds || rawAnnouncement.residentIds,
                    Staff_FullName: rawName || staff?.name,
                    StaffImage: staff?.image || usableImage(creator?.icon),
                };
            }));
            if (reset) announcementExtrasRef.current = extraAnnouncements;
            const announcementRows = reset ? extraAnnouncements : announcementExtrasRef.current;
            const namedFeedItems = filteredFeedItems.map((item) => {
                const existingName = usableName(item.Staff_FullName);
                const existingImage = usableImage(item.StaffImage);
                if (existingName && existingImage) return item;
                const fromIds = resolveStaff([
                    item.StaffId,
                    item.SenderId,
                    isMongoId(item.Staff_FullName) ? item.Staff_FullName : undefined,
                ]);
                const matchingExtra = item.Category === 'Announcement'
                    ? announcementRows.find((row) => isSameAnnouncement(item, row, facilityTimezone))
                    : undefined;
                const fromExtra = matchingExtra && usableName(matchingExtra.Staff_FullName)
                    ? { name: usableName(matchingExtra.Staff_FullName), image: matchingExtra.StaffImage }
                    : undefined;
                const staff = fromIds || fromExtra;
                if (!staff?.name && !staff?.image) return item;
                return {
                    ...item,
                    Staff_FullName: existingName || staff?.name,
                    StaffImage: existingImage || staff?.image,
                };
            });
            const listFeedRows = namedFeedItems.filter((item) => {
                if (item.Category !== 'Announcement' || usableName(item.Staff_FullName)) return true;
                return !announcementRows.some((row) => isSameAnnouncement(item, row, facilityTimezone));
            });
            const groupDroppedAnnouncements = serverFeedItems.filter((item) => (
                item.Category === 'Announcement'
                && !filteredFeedItems.some((row) => row._id === item._id)
            ));
            const uniqueAnnouncementRows = announcementRows.filter((extra) =>
                !listFeedRows.some((row) => isSameAnnouncement(row, extra, facilityTimezone) && usableName(row.Staff_FullName))
                && !groupDroppedAnnouncements.some((row) => isSameAnnouncement(row, extra, facilityTimezone)),
            );
            const newsItems: FeedItem[] = applyClientSideGroupFilter(await Promise.all(news.map(async (item, index) => {
                let image = usableImage(item.copyImageUrl);
                if (!image && item.signageImageId) {
                    image = await getStandaloneSignageImage({
                        facilityId: item.Facility || facilityId,
                        contentType: 'communityMessages',
                        contentId: item.signageImageId,
                    }).catch(() => undefined);
                }
                return {
                    _id: `${NEWS_ROW_PREFIX}${item._id || `${newsPage}-${index}`}`,
                    Category: 'CommunityNews',
                    FacilityId: item.Facility || facilityId,
                    DateAdded: item.startDate,
                    Details: item.name,
                    StaffId: item.AddedBy || undefined,
                    SenderId: item.AddedBy || undefined,
                    StaffImage: image,
                };
            })));
            const seenIds = new Set(reset ? [] : feedItemsRef.current.map((item) => item._id));
            const uniqueNewsItems = newsItems.filter((item) => {
                if (seenIds.has(item._id)) return false;
                seenIds.add(item._id);
                return true;
            });
            const extras = reset ? [...uniqueNewsItems, ...uniqueAnnouncementRows] : uniqueNewsItems;
            if (shouldFetchFeed) {
                setHasMoreByPage(serverFeedItems.length === PAGE_SIZE);
            }
            if (shouldFetchNews) {
                setHasMoreNews(hasSelectedGroup ? false : news.length === PAGE_SIZE);
            }
            let nextFeedItems: FeedItem[];
            let newListFeedCount: number;
            if (reset) {
                newListFeedCount = listFeedRows.length;
                nextFeedItems = [...listFeedRows, ...extras].sort(
                    (a, b) => toMillis(b.DateAdded, facilityTimezone) - toMillis(a.DateAdded, facilityTimezone),
                );
            } else {
                const prior = feedItemsRef.current;
                const newListFeed = listFeedRows.filter((item) => (
                    !prior.some((row) => row._id === item._id
                        || (item.Category === 'Announcement' && isSameAnnouncement(row, item, facilityTimezone)))
                ));
                newListFeedCount = newListFeed.length;
                nextFeedItems = [...prior, ...newListFeed, ...extras].sort(
                    (a, b) => toMillis(b.DateAdded, facilityTimezone) - toMillis(a.DateAdded, facilityTimezone),
                );
            }
            setFeedItems(nextFeedItems);
            // Kept in step now (not at the next render) so an automatic follow-up page below builds on it.
            feedItemsRef.current = nextFeedItems;

            // A group filter is applied here, after the server page: a full page can filter down to
            // nothing (comment lines on posts outside the group), which would leave Load More as a
            // dead end. Ask for the next page ourselves, a few times at most.
            if (shouldFetchFeed && hasSelectedGroup && serverFeedItems.length === PAGE_SIZE
                && newListFeedCount === 0 && emptyPagesLeft > 0) {
                const nextFeedPage = feedPage + 1;
                setFeedPageNo(nextFeedPage);
                await fetchFeed({
                    feedPage: nextFeedPage,
                    newsPage,
                    fetchFeedPage: true,
                    fetchNewsPage: false,
                    emptyPagesLeft: emptyPagesLeft - 1,
                });
            }
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Error fetching feed');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // Skip while this tab is hidden — refetches when isActive flips back to
        // true, so filter changes made while hidden are picked up on return.
        // Wait until FacilityContext has resolved so we don't fetch with an empty
        // timezone and then refetch when the real value arrives.
        if (!isActive || facilityLoading) return;
        setFeedPageNo(1);
        setNewsPageNo(1);
        fetchFeed({ feedPage: 1, newsPage: 1, reset: true });
    }, [isActive, facilityId, currentResidentId, groupResidentIds, groupStaffIds, selectedGroupIds, facilityLoading]);

    const showLoadMore = hasMoreByPage || hasMoreNews;

    const handleLoadMore = async () => {
        if (isLoading || !showLoadMore) return;
        const loadFeed = hasMoreByPage;
        const loadNews = hasMoreNews;
        const nextFeedPage = loadFeed ? feedPageNo + 1 : feedPageNo;
        const nextNewsPage = loadNews ? newsPageNo + 1 : newsPageNo;
        if (loadFeed) setFeedPageNo(nextFeedPage);
        if (loadNews) setNewsPageNo(nextNewsPage);
        await fetchFeed({
            feedPage: nextFeedPage,
            newsPage: nextNewsPage,
            fetchFeedPage: loadFeed,
            fetchNewsPage: loadNews,
        });
    };

    return (
        <div className="feed-view" style={{ position: 'relative', minHeight: 100 }}>
            {isLoading && feedItems.length === 0 && (
                <div style={{ padding: '40px 0', textAlign: 'center' }}>
                    <Loader active inline="centered">Loading</Loader>
                </div>
            )}

            {!isLoading && feedItems.length === 0 && (
                <div className="feed-empty-state">
                    <p>No feed activity yet.</p>
                </div>
            )}

            {feedItems.length > 0 && (
                <>
                    <div className="feed-list">
                        {feedItems.map(item => (
                            <FeedItemCard
                                key={item._id}
                                item={item}
                                onProfileClick={onProfileClick}
                                residentIdentityById={residentIdentityById}
                                facilityTimezone={facilityTimezone}
                            />
                        ))}
                    </div>
                    {showLoadMore && (
                        <div className="load-more-button">
                            <Button onClick={handleLoadMore} loading={isLoading} disabled={isLoading}>
                                Load More
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default FeedView;
